// src/misc/america250/CalendarDropdown.jsx

import React from "react";
import { safeStr } from "../utils/sheetRowUtils";
import {
  buildGoogleCalendarUrl,
  buildYahooCalendarUrl,
  buildIcsContent,
  downloadIcs,
} from "../utils/calendarUtils";

export function CalendarDropdown({
  title,
  description,
  location,
  startDate,
  endDate,
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const googleUrl =
    startDate &&
    buildGoogleCalendarUrl({
      title,
      description,
      location,
      startDate,
      endDate,
    });

  const yahooUrl =
    startDate &&
    buildYahooCalendarUrl({ title, description, location, startDate, endDate });

  return (
    <div className="relative inline-block w-full" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full border border-gray-300 text-gray-700 rounded px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
        aria-haspopup="menu"
        aria-expanded={open}
      >
          <span aria-hidden="true">🗓️</span>
        <span className="flex items-center gap-2">Add to Calendar</span>
        <span className="text-gray-500" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow-md overflow-hidden"
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-800"
            onClick={() => {
              setOpen(false);

              const icsText = buildIcsContent({
                title,
                description,
                location,
                startDate,
                endDate,
              });
              if (!icsText) return;

              const fileSafe = safeStr(title)
                .replace(/[^a-z0-9]+/gi, "_")
                .slice(0, 60);

              downloadIcs({
                filename: `${fileSafe || "event"}.ics`,
                icsText,
              });
            }}
            disabled={!startDate}
          >
            <span aria-hidden="true">🗓️</span>
            <span>Outlook</span>
          </button>

          <a
            role="menuitem"
            className={`block px-3 py-2 text-sm hover:bg-gray-50 ${
              googleUrl ? "text-gray-800" : "text-gray-400 pointer-events-none"
            } flex items-center gap-2`}
            href={googleUrl || "#"}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
          >
            <span aria-hidden="true">🅖</span>
            <span>Google Calendar</span>
          </a>

          <a
            role="menuitem"
            className={`block px-3 py-2 text-sm hover:bg-gray-50 ${
              yahooUrl ? "text-gray-800" : "text-gray-400 pointer-events-none"
            } flex items-center gap-2`}
            href={yahooUrl || "#"}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
          >
            <span aria-hidden="true">🅨</span>
            <span>Yahoo Calendar</span>
          </a>
        </div>
      )}
    </div>
  );
}
